// Why a page couldn't open (features/opening.md), in its own plain words, never the browser's: no signal
// and nothing kept on this device (the request got no answer at all, or the service worker had nothing
// kept for it: web/sw/sw.ts answers "offline"), or Tripmo not answering (a server error, the edge saying
// the box didn't answer, as while a release restarts it, or an answer that didn't come through whole). Anything else (not in the trip, a deleted trip,
// a link that's been replaced) is the page's own to say.

export type Failure = "no-signal" | "not-answering";

// What failed to open, as its page names it.
export type Opened = "tripmo" | "trip" | "trips" | "join";

export function failureOf(error: unknown): Failure | null {
  const status = (error as { status?: unknown } | null)?.status;
  // Not an answer from Tripmo at all (api.ts's ApiError carries one): nothing the person can do but try again.
  if (typeof status !== "number") return "not-answering";
  const data = (error as { data?: { error?: unknown } }).data;
  if (status === 0 || data?.error === "offline") return "no-signal";
  // An answer that didn't come through whole (api.ts, `answerOf`) is Tripmo not answering too.
  if (status >= 500 || data?.error === "unreadable") return "not-answering";
  return null;
}

export function failureWords(
  failure: Failure,
  what: Opened,
  device: "phone" | "computer",
): { title: string; body: string } {
  if (failure === "not-answering")
    return { title: "Tripmo isn't answering", body: "It may be restarting for a moment. Try again in a few seconds." };
  const body = {
    tripmo: `Tripmo needs a connection the first time it opens on this ${device}.`,
    trip: `This trip isn't kept on this ${device} yet. It opens once you're back online.`,
    trips: `Your trips aren't kept on this ${device} yet. They show once you're back online.`,
    join: "This join link needs a connection to open.",
  }[what];
  return { title: "No signal", body };
}
